import { useEffect, useMemo } from 'react'

import { Box, Button, FormHelperText, Grid, Stack, Tooltip } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'

import {
  TextInput,
  ControlledCheckbox,
  ControlledSelector,
  ISelectorOptions,
} from 'components/common'
import { WAIT_AND_RETURN_DISABLED_STATUSES } from 'constants/index'
import { SortEnumType, Tariff, useGetWaitAndReturnFeeCategoriesQuery } from 'generated/graphql'
import { emitter } from 'providers'
import { TBookingForm } from 'types'

import { isCollectionAndDeliveryAddressPopulated, getWaitAndReturnTooltipText } from './helpers'

interface IProps {
  tariffDetails?: Tariff
  isQuickQuote: boolean
  disabled?: boolean
  onSelectTariffClick: () => void
}

const InternalTariffForm = (props: IProps) => {
  const { tariffDetails, isQuickQuote, disabled, onSelectTariffClick } = props
  const {
    control,
    register,
    formState: { errors },
    setValue,
    clearErrors,
  } = useFormContext<TBookingForm>()

  const { data: waitAndReturnFeeCategoriesData } = useGetWaitAndReturnFeeCategoriesQuery({
    variables: {
      order: {
        percent: SortEnumType.Asc,
      },
    },
  })

  const waitAndReturnFeeCategoriesOptions: ISelectorOptions = useMemo(
    () =>
      waitAndReturnFeeCategoriesData?.waitAndReturnFeeCategories?.map((cat) => ({
        value: cat.id,
        label: cat.name,
      })) || [],
    [waitAndReturnFeeCategoriesData],
  )

  const [collectionAddresses, deliveryAddresses, isWaitAndReturn, status, customerId] = useWatch({
    name: ['collectionAddresses', 'deliveryAddresses', 'isWaitAndReturn', 'status', 'customerId'],
  })

  const isWaitAndReturnDisabled =
    disabled ||
    !customerId ||
    (collectionAddresses && collectionAddresses.length > 1) ||
    (status && WAIT_AND_RETURN_DISABLED_STATUSES.includes(status)) ||
    !isCollectionAndDeliveryAddressPopulated(collectionAddresses, deliveryAddresses)

  useEffect(() => {
    setValue('serviceType', tariffDetails?.xeroCode?.serviceType)
    emitter.emit('xero-service-type-change')
  }, [tariffDetails?.xeroCode?.serviceType, setValue])

  useEffect(() => {
    if (isWaitAndReturnDisabled && collectionAddresses && collectionAddresses.length > 1) {
      setValue('isWaitAndReturn', false)
    }
  }, [isWaitAndReturnDisabled, collectionAddresses, setValue])

  useEffect(() => {
    if (!isWaitAndReturn) {
      clearErrors('waitAndReturnFeeCategoryId')
    }
  }, [isWaitAndReturn, setValue, clearErrors])

  return (
    <Grid container spacing={1.5}>
      <Grid item xs={12} sm={6}>
        <TextInput label='Name' value={tariffDetails?.name} disabled={disabled} readOnly />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Stack alignItems='center'>
          <Button
            variant='contained'
            color='grey'
            disabled={disabled}
            onClick={onSelectTariffClick}>
            Select Tariff
          </Button>

          {errors.tariffId && (
            <FormHelperText error required focused>
              {errors.tariffId.message}
            </FormHelperText>
          )}

          {/* //* Used to focus on form error */}
          <Box sx={{ width: 0, height: 0, overflow: 'hidden' }}>
            {/* // ToDo: Fix double setValue */}
            <input {...register('tariffId')} />
          </Box>
        </Stack>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          label='Category'
          value={tariffDetails?.tariffCategory.name}
          disabled={disabled}
          readOnly
        />
      </Grid>

      {!isQuickQuote && (
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Tooltip
            title={getWaitAndReturnTooltipText(collectionAddresses, deliveryAddresses, customerId)}
            arrow
            placement='top'>
            <div>
              <ControlledCheckbox
                control={control}
                label='Wait &amp; Return'
                name='isWaitAndReturn'
                defaultValue={false}
                disabled={isWaitAndReturnDisabled}
              />
            </div>
          </Tooltip>

          <ControlledSelector
            control={control}
            label='Wait and Return Fee'
            name='waitAndReturnFeeCategoryId'
            options={waitAndReturnFeeCategoriesOptions}
            disabled={isWaitAndReturnDisabled || !isWaitAndReturn}
            error={!!errors?.waitAndReturnFeeCategoryId}
            helperText={errors?.waitAndReturnFeeCategoryId?.message}
            MenuProps={{
              slotProps: {
                paper: {
                  style: {
                    maxHeight: 200,
                  },
                },
              },
            }}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <TextInput label='Type' value={tariffDetails?.type} disabled={disabled} readOnly />
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput label='Tariff Id' value={tariffDetails?.id} disabled={disabled} readOnly />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label='Min. Charge'
          value={tariffDetails?.minCharge}
          disabled={disabled}
          readOnly
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput
          label='Min. Miles'
          value={tariffDetails?.minMiles}
          disabled={disabled}
          readOnly
        />
      </Grid>

      <Grid item xs={6}>
        <TextInput label='Rate' value={tariffDetails?.rate} disabled={disabled} readOnly />
      </Grid>

      <Grid item xs={6}>
        <TextInput label='Base Unit' value={tariffDetails?.baseUnit} disabled={disabled} readOnly />
      </Grid>
    </Grid>
  )
}

export default InternalTariffForm
